import Link from "next/link"
import {
  FaqAccordion,
  type FaqAccordionItem,
} from "@/components/ui/faq-accordion"
import { Button } from "@/components/ui/button"
import { JsonLd } from "@/components/ui/json-ld"
import { createFAQPageSchema } from "@/lib/schema"
import { mainEntityFor } from "@/lib/faq-canonical-page"
import { LpaGiftNote } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/lpa-gift"

export type { FaqAccordionItem }

export interface FaqSectionProps {
  heading: string
  /** Link label for "see all FAQs" button. Pass `null` to hide. */
  linkLabel?: string | null
  /** Link href. Caller should pass localePath("/faq"). */
  linkHref?: string
  items: FaqAccordionItem[]
  variant?: "light" | "dark"
  /** The section sits on the page grey: the "see all" button turns white so
   *  it shows (the blood ad pages, Eugene 21.10). */
  linkOnGrey?: boolean
  allowMultiple?: boolean
  /** Optional name for the FAQPage schema (e.g. "Insurance FAQ") */
  schemaName?: string
  /** Locale-prefixed base URL for schema URLs (e.g. "https://…/ch/de") */
  localeBase?: string
  /**
   * Page surface this section is rendered on, expressed in CMS-`pages`-field
   * vocabulary (e.g. "how-it-works", "insurance", "faq", "faq/<slug>").
   *
   * Used to filter which FAQs contribute to the FAQPage JSON-LD: only FAQs
   * whose canonical schema page (first entry of the FAQ's `pages` array,
   * falling back to `faq/<slug>`) equals `currentPage` are added to
   * `mainEntity`. Items whose canonical lives elsewhere are still rendered
   * in the visible accordion — they just don't get marked up here.
   *
   * If no FAQ qualifies, nothing is emitted (no empty `<script>` block).
   *
   * Why this exists: Google requires each FAQ Q&A to be marked up exactly
   * once site-wide. See `lib/faq-canonical-page.ts` for full rationale.
   */
  currentPage: string
  /** Tailwind max-width class for the inner two-column grid. Defaults to
   *  `max-w-[1440px]`; pages with tighter content rhythm (e.g. /reviews)
   *  pass `max-w-[1128px]` to align with their other section widths. */
  maxWidthClass?: string
  /**
   * `split` (default) — heading + "see all" button in a left column, the
   * accordion in a wider right column.
   * `stacked` — one narrow centred column: heading, accordion, then the
   * "see all" button. Used where the section has to line up with a
   * fixed-width card above it (e.g. the /insurance coverage checker), which
   * the two-column grid can't do.
   */
  layout?: "split" | "stacked"
  /** `stacked` only: width of the centred column. Defaults to 760px, the
   *  width of the insurance checker card. */
  columnClass?: string
}

export function FaqSection({
  heading,
  linkLabel,
  linkHref,
  items,
  variant = "light",
  linkOnGrey = false,
  allowMultiple = false,
  schemaName,
  localeBase,
  currentPage,
  maxWidthClass = "max-w-[1440px]",
  layout = "split",
  columnClass = "max-w-[760px]",
}: FaqSectionProps) {
  if (items.length === 0) return null

  const isDark = variant === "dark"

  // Only the FAQs canonical to *this* page surface contribute to the JSON-LD.
  // The full `items` list still drives the visible accordion below.
  const schemaItems = mainEntityFor(items, currentPage)

  return (
    <>
      {schemaItems.length > 0 && (
        <JsonLd
          data={createFAQPageSchema(schemaItems, {
            ...(schemaName ? { name: schemaName } : {}),
            ...(localeBase ? { localeBase } : {}),
          })}
        />
      )}
      <section
        className={`lazy-section py-16 md:py-24 ${isDark ? "bg-foreground" : ""}`}
      >
        {layout === "stacked" ? (
          <div className={`mx-auto ${maxWidthClass} px-4 md:px-10`}>
            <div className={`mx-auto flex w-full ${columnClass} flex-col`}>
              <h2
                className={
                  isDark
                    ? "type-display-sm text-white"
                    : "type-display-sm text-neutral-900"
                }
              >
                {heading}
              </h2>
              <div className="mt-8">
                <FaqAccordion
                  items={items}
                  variant={variant}
                  allowMultiple={allowMultiple}
                />
              </div>
              {linkLabel && linkHref && (
                <Link href={linkHref} className="mt-8 self-start">
                  <Button
                    variant="outline"
                    className={
                      isDark
                        ? "border-white/30 text-white hover:border-white hover:bg-white/10"
                        : linkOnGrey
                          ? "bg-white hover:bg-neutral-100"
                          : undefined
                    }
                  >
                    {linkLabel}
                  </Button>
                </Link>
              )}
              {!isDark && <LpaGiftNote place="faq" className="mt-8" />}
            </div>
          </div>
        ) : (
          <div
            className={`mx-auto grid ${maxWidthClass} grid-cols-1 gap-8 px-4 md:grid-cols-[1fr_2fr] md:px-10`}
          >
            {/* Left column — heading at top, "see all" link pinned to the
              bottom of the column on md+ via `mt-auto`. Slide-in
              arrow: label nudges left while the arrow slides in from
              the right with fade. Lighter `border-neutral-100`
              stroke at rest. */}
            <div className="flex flex-col md:min-h-[12rem]">
              <h2
                className={
                  isDark
                    ? "type-display-sm text-white"
                    : "type-display-sm text-neutral-900"
                }
              >
                {heading}
              </h2>
              {linkLabel && linkHref && (
                <Link href={linkHref} className="mt-6 self-start md:mt-auto">
                  <Button
                    variant="outline"
                    className={
                      isDark
                        ? "border-white/30 text-white hover:border-white hover:bg-white/10"
                        : linkOnGrey
                          ? "bg-white hover:bg-neutral-100"
                          : undefined
                    }
                  >
                    {linkLabel}
                  </Button>
                </Link>
              )}
            </div>

            {/* Right column */}
            <FaqAccordion
              items={items}
              variant={variant}
              allowMultiple={allowMultiple}
            />
            {/* The free Lp(a)'s small print, under the questions. */}
            {!isDark && <LpaGiftNote place="faq" className="md:col-span-2" />}
          </div>
        )}
      </section>
    </>
  )
}
